// src/components/FeaturedShops.js - Mobile-optimiert
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Spin, Alert, Rate, Tag, Button, Empty, Image, Space, Carousel } from 'antd';
import { EnvironmentOutlined, CheckCircleOutlined, EyeOutlined, TagOutlined, StarFilled } from '@ant-design/icons';
import { fetchFeaturedShops } from '../services/shopService';
import { useNavigate } from 'react-router-dom';

const { Meta } = Card;
const { Text, Title } = Typography;

const FeaturedShops = () => {
    const [groupedCityData, setGroupedCityData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const loadGroupedFeaturedShops = async () => {
            try {
                console.log('FeaturedShops: Loading grouped featured shops from backend...');
                setLoading(true);
                setError(null);
                setGroupedCityData([]);

                const response = await fetchFeaturedShops();
                console.log('FeaturedShops: Response from fetchFeaturedShops (grouped):', response);

                if (response.success && Array.isArray(response.data)) {
                    const isValidData = response.data.every(item => 
                        item && typeof item.city === 'string' && Array.isArray(item.shops)
                    );

                    if (isValidData) {
                        setGroupedCityData(response.data);
                        console.log(`FeaturedShops: Loaded grouped data for ${response.data.length} cities.`);
                    } else {
                         console.error('FeaturedShops: Received data is not in the expected format [{city, shops: [...]}]');
                         setError('Fehler: Ungültiges Datenformat vom Server erhalten.');
                    }
                    
                } else {
                    const errMsg = response?.message || 'Unbekannter Fehler beim Laden der gruppierten Shops';
                    console.error('FeaturedShops: Failed to load grouped shops:', errMsg);
                    setError(errMsg);
                }
            } catch (err) {
                console.error('FeaturedShops: Error loading grouped shops:', err);
                setError('Ein Netzwerkfehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
            } finally {
                setLoading(false);
            }
        };

        loadGroupedFeaturedShops();
    }, []);

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px 0', background: '#f9f9f9' }}>
                <Spin tip="Lade empfohlene Shops..." size="large" />
            </div>
        );
    }

    if (error) {
        return (
             <div style={{ padding: '50px 0', background: '#f9f9f9' }}>
                 <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                    <Alert
                        message="Fehler beim Laden"
                        description={error}
                        type="error"
                        showIcon
                        action={
                            <Button type="primary" onClick={() => window.location.reload()}>
                                Erneut versuchen
                            </Button>
                        }
                     />
                 </div>
             </div>
        );
    }

    if (groupedCityData.length === 0) {
        return (
            <div style={{ padding: '50px 0', background: '#f9f9f9' }}>
                 <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
                    <Empty description="Aktuell sind keine Shops für die Anzeige auf der Startseite verfügbar.">
                    </Empty>
                </div>
            </div>
        );
    }

    return (
        <div style={{ padding: '50px 0', background: '#f9f9f9' }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
                <Title level={2} style={{ textAlign: 'center', marginBottom: '48px' }}>
                    Top Shops in Ihrer Nähe
                </Title>
                <Carousel
                    autoplay
                    dots={{ className: 'custom-carousel-dots' }}
                    arrows={true}
                >
                    {groupedCityData.map(({ city, shops }) => (
                        <div key={city}>
                            <Title level={4} style={{ textAlign: 'center', marginBottom: '32px', textTransform: 'capitalize' }}>
                                {city}
                            </Title>
                            <Row gutter={[24, 24]} justify="center" style={{ padding: '0 5%' }}>
                                {shops.map(shop => {
                                    const colStyle = {
                                        display: 'flex',
                                        minWidth: '290px'
                                    };
                                    return (
                                        <Col key={shop.id} xs={24} sm={12} md={8} style={colStyle}>
                                            <Card
                                                hoverable
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.09)',
                                                    transition: 'all 0.3s ease',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#fff'
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.12)';
                                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.09)';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                                cover={
                                                    <div style={{ height: '180px', overflow: 'hidden', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
                                                        <Image
                                                            alt={shop.name}
                                                            src={shop.images?.[0]?.url || '/images/placeholder-shop.svg'}
                                                            fallback='/images/placeholder-shop.svg'
                                                            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                                                            preview={false}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                }
                                                actions={[
                                                    <Button
                                                        type="primary"
                                                        icon={<EyeOutlined />}
                                                        onClick={() => navigate(`/shops/${shop.id}`)}
                                                        key="view"
                                                    >
                                                        Ansehen
                                                    </Button>
                                                ]}
                                            >
                                                <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '20px' }}>
                                                    <div>
                                                        <Meta
                                                            title={<Title level={5} ellipsis={{ rows: 2, tooltip: shop.name }} style={{marginBottom: '6px', minHeight: '2.6em', fontWeight: '600' }}>{shop.name}</Title>}
                                                            description={<Text ellipsis={{tooltip: `${shop.address?.street || ''}, ${shop.address?.postalCode || ''} ${city}`}} type="secondary"><EnvironmentOutlined style={{ marginRight: '5px' }} /> {city}</Text>}
                                                        />
                                                        <Space align="center" style={{ marginTop: '10px', whiteSpace: 'nowrap' }}>
                                                            <Rate allowHalf disabled value={shop.rating || 0} style={{ fontSize: '1rem'}} />
                                                            <Text type="secondary" style={{ fontSize: '0.9em' }}>
                                                                ({shop.rating?.toFixed(1) || '0.0'}) ({shop.reviewCount || 0} Bew.)
                                                            </Text>
                                                        </Space>
                                                    </div>
                                                    <Space direction="vertical" style={{ marginTop: '15px', width: '100%' }}>
                                                        <Space wrap size={[4, 6]} style={{ minHeight: '22px' }}>
                                                            {shop.subscriptionPlan === 'professional' && (
                                                                 <Tag color="gold" icon={<StarFilled />}>Professional</Tag>
                                                            )}
                                                            {shop.isVerified && (
                                                                <Tag color="success" icon={<CheckCircleOutlined />}>Verifiziert</Tag>
                                                            )}
                                                        </Space>
                                                        {shop.brands && shop.brands.length > 0 && (
                                                            <Space wrap size={[4, 4]}>
                                                                <TagOutlined style={{ color: 'rgba(0,0,0,0.45)', marginRight: '4px' }} />
                                                                {shop.brands.slice(0, 3).map((brand, index) => (
                                                                    <Tag key={index} color="blue">{brand}</Tag>
                                                                ))}
                                                                {shop.brands.length > 3 && (
                                                                    <Tag>+{shop.brands.length - 3} weitere</Tag>
                                                                )}
                                                            </Space>
                                                        )}
                                                    </Space>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                            <div style={{ height: '30px' }}></div>
                        </div>
                    ))}
                </Carousel>
            </div>
            <style>{`
                .custom-carousel-dots li button {
                    background: #ccc !important;
                    opacity: 0.7;
                    height: 10px !important;
                    width: 10px !important;
                    border-radius: 50%;
                }
                .custom-carousel-dots li.slick-active button {
                    background: #1890ff !important;
                    opacity: 1;
                }
                
                .ant-carousel .slick-prev, 
                .ant-carousel .slick-next {
                    font-size: 20px;
                    color: #555;
                    background-color: rgba(255, 255, 255, 0.7);
                    border-radius: 50%;
                    width: 35px;
                    height: 35px;
                    line-height: 35px; 
                    z-index: 1;
                    transition: all 0.3s ease;
                }
                .ant-carousel .slick-prev:hover,
                .ant-carousel .slick-next:hover {
                    color: #1890ff;
                    background-color: rgba(255, 255, 255, 0.9);
                }
                .ant-carousel .slick-prev {
                    left: -45px;
                }
                .ant-carousel .slick-next {
                    right: -45px;
                }
                
                @media (max-width: 768px) {
                    .ant-carousel .slick-prev {
                        left: 10px;
                    }
                    .ant-carousel .slick-next {
                        right: 10px;
                    }
                 }
                 .ant-card-hoverable {
                     position: relative;
                 }
            `}</style>
        </div>
    );
};

export default FeaturedShops;